import React, { useEffect, useState, useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import apiService from '../services/api';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import { Package, RefreshCw, Play } from 'lucide-react';
import './KiaraImportsPage.css';

interface KiaraRun {
  id: number;
  date_from: string;
  date_to: string;
  status: string;
  trigger: string;
  started_at: string;
  finished_at?: string | null;
  error?: string | null;
  success_reports?: number;
  failed_reports?: number;
}

const RetryBtnRenderer = (params: any) => {
  const { handleRetry, actionLoading } = params.context || {};
  return (
    <button
      className="btn btn-outline-primary"
      style={{ padding: '2px 10px', fontSize: '0.82rem' }}
      onClick={() => handleRetry && handleRetry(params.data.id)}
      disabled={!!actionLoading}
    >
      Riprova
    </button>
  );
};

const KiaraImportsPage: React.FC = () => {
  const [runs, setRuns] = useState<KiaraRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [manualDate, setManualDate] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadRuns = async () => {
    try {
      setLoading(true);
      const response = await apiService.getKiaraImportRuns(60);
      setRuns(response?.runs || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento degli import Kiara');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRuns();
  }, []);

  const handleRetry = async (runId: number) => {
    try {
      setActionLoading(true);
      await apiService.retryKiaraImportRun(runId);
      await loadRuns();
    } catch (err: any) {
      setError(err.message || 'Errore durante il retry');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManualRun = async () => {
    if (!manualDate) {
      setError('Seleziona una data valida');
      return;
    }
    try {
      setActionLoading(true);
      await apiService.runKiaraImportForDate(manualDate);
      setManualDate('');
      await loadRuns();
    } catch (err: any) {
      setError(err.message || 'Errore durante il run manuale');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (value: string) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('it-IT');
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('it-IT');
  };

  const columnDefs = useMemo<ColDef[]>(() => [
    { headerName: 'Data', field: 'date_from', valueFormatter: p => formatDate(p.value), minWidth: 110, maxWidth: 130 },
    { headerName: 'Stato', field: 'status', minWidth: 100, maxWidth: 130,
      cellStyle: (p: any): any => {
        if (p.value === 'success') return { color: '#28a745', fontWeight: 600 };
        if (p.value === 'error') return { color: '#dc3545', fontWeight: 600 };
        if (p.value === 'running') return { color: '#007bff', fontWeight: 600 };
        return null;
      }
    },
    { headerName: 'Trigger', field: 'trigger', minWidth: 100, maxWidth: 140 },
    { headerName: 'Avviato', field: 'started_at', valueFormatter: p => formatDateTime(p.value), minWidth: 150 },
    { headerName: 'Completato', field: 'finished_at', valueFormatter: p => formatDateTime(p.value), minWidth: 150 },
    { headerName: 'Report OK', field: 'success_reports', minWidth: 110, maxWidth: 130,
      valueFormatter: p => p.value ?? 0 },
    { headerName: 'Report KO', field: 'failed_reports', minWidth: 110, maxWidth: 130,
      valueFormatter: p => p.value ?? 0,
      cellStyle: (p: any) => (p.value > 0 ? { color: '#dc3545', fontWeight: 600 } : null) },
    { headerName: 'Errore', field: 'error', minWidth: 200, flex: 2,
      valueFormatter: p => p.value || '-',
      cellStyle: (p: any): any => (p.value ? { color: '#dc3545', fontSize: '0.82rem' } : { color: '#999' }) },
    { headerName: 'Azioni', field: 'id', minWidth: 110, maxWidth: 120,
      sortable: false, filter: false,
      cellRenderer: RetryBtnRenderer }
  ], []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleRetry è ricreata ad ogni render; includerla annullerebbe la memoizzazione del context
  const gridContext = useMemo(() => ({ handleRetry, actionLoading }), [actionLoading]);

  return (
    <div className="kiara-imports-page">
      <div className="page-header">
        <h1>
          <Package size={20} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Import Kiara
        </h1>
        <button className="btn btn-outline-primary" onClick={loadRuns} disabled={loading}>
          <RefreshCw size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          {loading ? 'Aggiornamento...' : 'Aggiorna'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="manual-run">
        <div>
          <label htmlFor="manualDate">Avvia import per data</label>
          <input
            id="manualDate"
            type="date"
            value={manualDate}
            onChange={(event) => setManualDate(event.target.value)}
          />
        </div>
        <button
          className="btn btn-secondary"
          onClick={handleManualRun}
          disabled={actionLoading}
        >
          <Play size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          {actionLoading ? 'In corso...' : 'Avvia'}
        </button>
      </div>

      <GelatoDataGrid
        rowData={runs}
        columnDefs={columnDefs}
        loading={loading}
        paginationPageSize={50}
        context={gridContext}
        height="600px"
      />
    </div>
  );
};

export default KiaraImportsPage;

